import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useBlocker } from 'react-router-dom';
import { useTheme } from '@course-authoring/shared-types';
import { useClientContext } from './useClientContext';
import { ModalShell } from '../components/ModalShell';

interface ThemeSettingsNavigationGuardOptions {
  isDirty: boolean;
  isSaving: boolean;
  onLeave: () => void;
}

type ConfirmationOwner = 'route' | 'context' | null;

interface PendingContextIntent {
  resolve: (allowed: boolean) => void;
}

/** Opt-in departure protection for Theme Settings only. */
export function useThemeSettingsNavigationGuard({
  isDirty,
  isSaving,
  onLeave,
}: ThemeSettingsNavigationGuardOptions): { confirmationDialog: ReactNode } {
  const { registerClientContextChangeGuard } = useClientContext();
  const { tokens } = useTheme();
  const shouldBlock = isDirty || isSaving;
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    shouldBlock && currentLocation.pathname !== nextLocation.pathname
  );
  const blockerRef = useRef(blocker);
  const [owner, setOwner] = useState<ConfirmationOwner>(null);
  const pendingContextRef = useRef<PendingContextIntent | null>(null);
  const suppressBlockedRoutePromptRef = useRef(false);
  const shouldBlockRef = useRef(shouldBlock);
  const onLeaveRef = useRef(onLeave);
  const titleId = useId();

  shouldBlockRef.current = shouldBlock;
  blockerRef.current = blocker;
  onLeaveRef.current = onLeave;

  const settleContext = useCallback((allowed: boolean) => {
    const pending = pendingContextRef.current;
    pendingContextRef.current = null;
    pending?.resolve(allowed);
  }, []);

  useEffect(() => {
    if (blocker.state !== 'blocked') {
      suppressBlockedRoutePromptRef.current = false;
      return;
    }
    if (suppressBlockedRoutePromptRef.current) {
      suppressBlockedRoutePromptRef.current = false;
      return;
    }
    if (pendingContextRef.current) {
      // A newer route intent replaces an outstanding account selection.
      settleContext(false);
    }
    setOwner('route');
  }, [blocker, settleContext]);

  useEffect(() => {
    if (!shouldBlock) {
      const activeBlocker = blockerRef.current;
      if (activeBlocker.state === 'blocked') {
        suppressBlockedRoutePromptRef.current = true;
        activeBlocker.reset();
      }
      settleContext(false);
      setOwner(null);
    }
  }, [blocker, shouldBlock, settleContext]);

  useEffect(() => {
    if (!shouldBlock) return undefined;
    const unregister = registerClientContextChangeGuard?.(() => {
      if (!shouldBlockRef.current) return true;

      // A new context request replaces any route/context confirmation already
      // waiting, and the older transition is explicitly declined.
      const activeBlocker = blockerRef.current;
      if (activeBlocker.state === 'blocked') {
        suppressBlockedRoutePromptRef.current = true;
        activeBlocker.reset();
      }
      settleContext(false);
      setOwner('context');
      return new Promise<boolean>((resolve) => {
        pendingContextRef.current = { resolve };
      });
    });
    return unregister;
  }, [blocker, registerClientContextChangeGuard, settleContext, shouldBlock]);

  useEffect(() => {
    if (!shouldBlock) return undefined;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [shouldBlock]);

  useEffect(() => () => {
    settleContext(false);
  }, [settleContext]);

  const stay = useCallback(() => {
    if (owner === 'route' && blocker.state === 'blocked') blocker.reset();
    if (owner === 'context') settleContext(false);
    setOwner(null);
  }, [blocker, owner, settleContext]);

  const leave = useCallback(() => {
    onLeaveRef.current();
    if (owner === 'route' && blocker.state === 'blocked') blocker.proceed();
    if (owner === 'context') settleContext(true);
    setOwner(null);
  }, [blocker, owner, settleContext]);

  const confirmationDialog = (
    <ModalShell
      isOpen={owner !== null}
      onClose={stay}
      ariaLabelledBy={titleId}
      width="440px"
      disableBackdropClose
    >
      <div style={{ padding: '24px' }}>
        <h2 id={titleId} style={{ color: tokens.text.primary, margin: '0 0 12px' }}>
          Leave Theme Settings?
        </h2>
        <p style={{ color: tokens.text.muted, margin: '0 0 24px' }}>
          {isSaving
            ? 'A save is in progress and may still complete after you leave. Your remaining unsaved changes will be discarded.'
            : 'Your unsaved changes will be discarded.'}
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <button type="button" onClick={stay} style={{
            minHeight: '36px',
            padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
            border: `1px solid ${tokens.borders.default}`,
            borderRadius: tokens.radius.sm,
            backgroundColor: tokens.backgrounds.surface,
            color: tokens.text.primary,
            font: 'inherit',
            cursor: 'pointer',
          }}>
            Stay on page
          </button>
          <button type="button" onClick={leave} style={{
            minHeight: '36px',
            padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
            border: 'none',
            borderRadius: tokens.radius.sm,
            backgroundColor: tokens.buttons.action.bg,
            color: tokens.text.onBrand,
            font: 'inherit',
            fontWeight: 600,
            cursor: 'pointer',
          }}>
            Leave without saving
          </button>
        </div>
      </div>
    </ModalShell>
  );

  return { confirmationDialog };
}
